import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  Chip,
  Collapse,
  Divider,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { SITE_NAME } from '../constants/site';

// 継続支援（サブスク）で得られる代表的な価値
const SUBSCRIPTION_POINTS = [
  '便利な追加機能をご利用いただけます',
  '継続的なご支援が、もっとも大きな力になります',
  'いつでもプランの変更・解約が可能です',
];

// 単発支援の金額プリセット
const ONE_TIME_PRESETS = [300, 500, 1000, 3000, 5000];

// いただいた支援の使い道（透明性）
const USE_OF_FUNDS = [
  {
    icon: <StorageIcon />,
    title: 'サービスの提供',
    description:
      '膨大なデータベースやユーザーのライブラリデータの維持費、開発に係る費用など、その大半を開発者個人の資金で支えている状況です。これだけのデータを安定してお届けするには、相応の費用が継続的に必要です。',
  },
  {
    icon: <CodeIcon />,
    title: '開発リソースの確保',
    description:
      '皆様のサポートは、開発スピードを劇的に上げるための原動力になります。収益のみで安定してサービスの維持・開発が続けられる自走可能な状態を最短距離で達成することを目指しています。',
  },
  {
    icon: <RocketLaunchIcon />,
    title: 'プロモーション',
    description:
      'PULSEは、利用するユーザーが増えれば増えるほどデータが充実し、より楽しく便利で価値のあるものになります。多くのユーザーに認知を広げ、単なるツールから「熱気のあるコミュニティ」に進化させるために力を貸してください。',
  },
];

const SupportPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';

  const [storyOpen, setStoryOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(500);
  const [customAmount, setCustomAmount] = useState('');
  const [snackOpen, setSnackOpen] = useState(false);

  // カード共通スタイル（最近のページの質感に合わせた半透明＋ブラー）
  const glassCard = {
    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    borderRadius: '20px',
  } as const;

  const effectiveAmount = customAmount
    ? Number(customAmount.replace(/[^\d]/g, '')) || null
    : selectedAmount;

  const handleOneTimeSupport = () => {
    // 単発支援のStripe連携は未実装。UIのみ用意し、準備中である旨を明示する。
    setSnackOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>PULSEを支援する | {SITE_NAME}</title>
        <meta
          name="description"
          content="PULSEは、そのほとんどを一人で開発・運営しているサービスです。継続・単発のご支援を通じて、日本発のゲーム評価基盤づくりを一緒に育てていくことができます。"
        />
      </Helmet>

      {/* ===== Hero ===== */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: 'background.default',
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 10 },
        }}
      >
        {/* 背景のやわらかい光（地色へ自然に溶ける・切り替わり線を作らない） */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(120% 90% at 50% -10%, ${alpha(theme.palette.primary.main, 0.13)} 0%, transparent 60%),
                         radial-gradient(70% 60% at 12% 8%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 55%),
                         radial-gradient(70% 60% at 88% 20%, ${alpha(theme.palette.secondary.main, 0.07)} 0%, transparent 55%)`,
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Chip
              icon={<FavoriteBorderIcon sx={{ fontSize: '1rem !important' }} />}
              label="SUPPORT PULSE"
              sx={{
                px: 1,
                fontWeight: 700,
                letterSpacing: 1.5,
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                '& .MuiChip-icon': { color: 'primary.main' },
              }}
            />
            <Typography
              variant="h3"
              component="h1"
              sx={{ fontWeight: 800, lineHeight: 1.3, fontSize: { xs: '1.9rem', md: '2.75rem' } }}
            >
              PULSEを、もっと先へ。
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                maxWidth: 620,
                fontSize: { xs: '0.95rem', md: '1.05rem' },
                lineHeight: 1.9,
              }}
            >
              いつもPULSEをご利用いただき、ありがとうございます。
              PULSEは、そのほとんどを一人で開発・運営しているサービスです。
              もしこの場所に価値を感じていただけたなら、その運営を支える形で力を貸していただけると嬉しく思います。
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/pricing')}
                sx={{ px: 4, py: 1.25, fontWeight: 700, borderRadius: '12px', bgcolor: 'primary.main', color: '#000' }}
              >
                継続して支える
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="#one-time"
                sx={{
                  px: 4,
                  py: 1.25,
                  fontWeight: 700,
                  borderRadius: '12px',
                  borderColor: alpha(theme.palette.text.primary, 0.25),
                  color: 'text.primary',
                }}
              >
                単発で応援する
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        {/* ===== 開発者からのメッセージ（能動的に読み込むストーリー） ===== */}
        <Paper elevation={0} sx={{ ...glassCard, p: { xs: 3, md: 5 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
                fontWeight: 800,
                fontSize: '1.1rem',
              }}
            >
              U
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>開発者からのメッセージ</Typography>
              <Typography variant="caption" color="text.secondary">
                PULSE 開発・運営 / 梅津
              </Typography>
            </Box>
          </Stack>

          <Typography sx={{ color: 'text.secondary', lineHeight: 2, mb: 1 }}>
            あらためまして、PULSEの開発・運営を行っている梅津と申します。
            運営面ではデータベースの整備などに協力してくれる方もいますが、PULSEは実質的に、一人の開発者によって進めているプロジェクトです。
            膨大なデータベースやユーザーのライブラリの維持費、開発にかかる費用は、
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>現在も私個人の資金で支えています</Box>。
          </Typography>

          <Collapse in={storyOpen} timeout="auto">
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                なぜPULSEをつくったのか
              </Typography>
              <Typography sx={{ color: 'text.secondary', lineHeight: 2, mb: 2.5 }}>
                日本は世界のゲーム産業を牽引する国の一つであり、数多くの名作を世界に送り出してきました。
                しかし、信頼性や権威性の高いゲーム評価基盤は、日本にはいまだ存在しません。国内のゲームメディアでも、海外のMetacriticなどのスコアを参照するのが一般的です。
              </Typography>
              <Typography sx={{ color: 'text.secondary', lineHeight: 2, mb: 2.5 }}>
                その背景には、純粋なレビューサイトには「評価」に関心のある人しか集まらない、という構造的な問題がありました。
                そこでPULSEは、<Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>ゲームを記録・管理するツール</Box>としての価値を提供することで、これまで評価に関心のなかった方も巻き込み、幅広いユーザーの評価を自然に集めていきます。
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                目指している未来
              </Typography>
              <Typography sx={{ color: 'text.secondary', lineHeight: 2, mb: 2.5 }}>
                人生を通してプレイしてきたゲームを記録し、管理する。その過程で、評価が自然と蓄積されていく。
                これは純粋なレビュープラットフォームでは達成できないアプローチであり、同時にスコアの信頼性を高める仕組みでもあります。
                この仕組みを通じて、<Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>日本発の信頼できるゲーム評価基盤</Box>を確立したいと考えています。
              </Typography>

              <Box
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.9 }}>
                  PULSEの基本的な機能は、これからも無料で使い続けられます。ご支援はあくまで任意のものです。
                  ただ、ほぼ一人で開発を続けている以上、すべてを思いどおりの速さで進められるわけではないのも、正直なところです。
                  <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                    　いただくご支援は開発を大きく後押しし、初期構想の半分にも満たない残りの機能を、より早くお届けするための力になります。
                  </Box>
                </Typography>
              </Box>
            </Box>
          </Collapse>

          <Button
            onClick={() => setStoryOpen((v) => !v)}
            endIcon={
              <ExpandMoreIcon
                sx={{ transform: storyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
              />
            }
            sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, color: 'primary.main' }}
          >
            {storyOpen ? '閉じる' : 'PULSEに込めた想いと、目指す未来を読む'}
          </Button>
        </Paper>

        {/* ===== 数字で見るPULSE ===== */}
        <Box sx={{ mt: { xs: 5, md: 7 } }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>
            これまでの歩み
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            広告を打たず、口コミだけで。
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(3, 1fr)' },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            {[
              { value: '1万+', label: '登録ユーザー' },
              { value: '120万+', label: '累計ゲーム記録' },
              { value: '30万+', label: '収録タイトル' },
            ].map((stat) => (
              <Paper key={stat.label} elevation={0} sx={{ ...glassCard, p: { xs: 2, md: 3 }, textAlign: 'center' }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.5rem', md: '2.1rem' },
                    color: 'primary.main',
                    lineHeight: 1.2,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
                  {stat.label}
                </Typography>
              </Paper>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            ※ 2026年7月時点。正式リリースから1年あまりで蓄積された記録です。
          </Typography>
        </Box>

        {/* ===== 支援の2つの道 ===== */}
        <Box sx={{ mt: { xs: 6, md: 9 } }}>
          <Stack alignItems="center" textAlign="center" spacing={1} sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              支援という選択肢
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
              続けて支えるか、一度だけ応援するか。あなたに合った形で構いません。
            </Typography>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            {/* --- 継続支援（サブスク・おすすめ） --- */}
            <Paper
              elevation={0}
              sx={{
                ...glassCard,
                p: { xs: 3, md: 4 },
                position: 'relative',
                border: `1.5px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Chip
                label="おすすめ"
                size="small"
                sx={{
                  position: 'absolute',
                  top: -12,
                  left: 24,
                  bgcolor: 'primary.main',
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                }}
              />
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  継続して支える
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.9 }}>
                PULSE PRO にご登録いただくと、便利な機能をご利用いただきながら、もっとも確実な形で開発を支えていただけます。
                継続的なご支援は、何より心強い支えになります。
              </Typography>
              <Stack spacing={1} sx={{ mb: 3 }}>
                {SUBSCRIPTION_POINTS.map((p) => (
                  <Stack key={p} direction="row" spacing={1} alignItems="flex-start">
                    <CheckIcon sx={{ fontSize: '1.05rem', color: 'primary.main', mt: '2px' }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {p}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Button
                component={RouterLink}
                to="/pricing"
                variant="contained"
                fullWidth
                endIcon={<ArrowForwardIcon />}
                sx={{ mt: 'auto', py: 1.15, fontWeight: 700, borderRadius: '12px', bgcolor: 'primary.main', color: '#000' }}
              >
                プランを見る
              </Button>
            </Paper>

            {/* --- 単発支援（UIのみ・Stripeは準備中） --- */}
            <Paper
              id="one-time"
              elevation={0}
              sx={{ ...glassCard, p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', scrollMarginTop: 80 }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                <VolunteerActivismIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  一度だけ応援する
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.9 }}>
                一度きりのご支援でも、大きな励みになります。
                金額をお選びのうえ、気軽に応援していただけます。
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1,
                  mb: 1.5,
                }}
              >
                {ONE_TIME_PRESETS.map((amount) => {
                  const active = !customAmount && selectedAmount === amount;
                  return (
                    <Button
                      key={amount}
                      onClick={() => {
                        setSelectedAmount(amount);
                        setCustomAmount('');
                      }}
                      sx={{
                        py: 1,
                        fontWeight: 700,
                        borderRadius: '10px',
                        textTransform: 'none',
                        border: `1px solid ${active ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.15)}`,
                        bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                        color: active ? 'primary.main' : 'text.primary',
                        '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.06) },
                      }}
                    >
                      ¥{amount.toLocaleString()}
                    </Button>
                  );
                })}
              </Box>

              <TextField
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  if (e.target.value) setSelectedAmount(null);
                }}
                placeholder="自由に入力"
                size="small"
                fullWidth
                inputProps={{ inputMode: 'numeric' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                }}
                sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />

              <Button
                variant="outlined"
                fullWidth
                onClick={handleOneTimeSupport}
                disabled={!effectiveAmount || effectiveAmount <= 0}
                startIcon={<FavoriteBorderIcon />}
                sx={{
                  mt: 'auto',
                  py: 1.15,
                  fontWeight: 700,
                  borderRadius: '12px',
                  borderColor: alpha(theme.palette.text.primary, 0.25),
                  color: 'text.primary',
                }}
              >
                {effectiveAmount ? `¥${effectiveAmount.toLocaleString()} を支援する` : '金額を選択'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                単発でのご支援は現在準備中です
              </Typography>
            </Paper>
          </Box>
        </Box>

        {/* ===== 支援金の使い道（透明性） ===== */}
        <Box sx={{ mt: { xs: 6, md: 9 } }}>
          <Stack alignItems="center" textAlign="center" spacing={1} sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              いただいた支援の使い道
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
              あなたの支援が、具体的に何を支えるのか。正直にお伝えします。
            </Typography>
          </Stack>
          <Stack spacing={2}>
            {USE_OF_FUNDS.map((item) => (
              <Paper key={item.title} elevation={0} sx={{ ...glassCard, p: { xs: 2.5, md: 3 } }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.9 }}>
                      {item.description}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>

        {/* ===== クロージング ===== */}
        <Paper
          elevation={0}
          sx={{
            ...glassCard,
            mt: { xs: 6, md: 9 },
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            background: `linear-gradient(160deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'} 100%)`,
          }}
        >
          <FavoriteBorderIcon sx={{ color: 'primary.main', fontSize: '2rem', mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            まずは、使ってくださってありがとうございます。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', lineHeight: 2, mb: 3 }}>
            PULSEを日々使っていただけること、それ自体が何よりの支えです。
            そのうえで、この場所を一緒に育てていきたいと感じていただけたなら、これほど嬉しいことはありません。
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/pricing')}
            sx={{ px: 4, py: 1.15, fontWeight: 700, borderRadius: '12px', bgcolor: 'primary.main', color: '#000' }}
          >
            PULSE PRO を見る
          </Button>
        </Paper>

        <Divider sx={{ mt: 6, mb: 2, opacity: 0.4 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
          運営元：Pulse development / お問い合わせ：info@dev.plsdb.com
        </Typography>
      </Container>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          単発でのご支援は現在準備中です。もうしばらくお待ちください。
        </Alert>
      </Snackbar>
    </>
  );
};

export default SupportPage;
